import type { AppState, EditorGraphSlot } from '../state/appState.js';
import type { EditorCommands } from '../editor/commands.js';
import {
  getSlotStateFor,
  libraryNameFor,
  type LibraryKind,
  type SlotValue,
} from './editorTargetInfo.js';

interface EditorSidebarDeps {
  appState: AppState;
  syncEditor: () => boolean;
  commands: Pick<EditorCommands, 'attachLibrary' | 'detachLibrary' | 'saveToLibrary' | 'createLibraryItem'>;
  openComponentSlotEditor(componentId: string | null, slot: EditorGraphSlot, syncBefore?: boolean, layerId?: string): void;
  renderSceneTree: () => void;
  renderEditorNavigation: () => void;
}

export interface EditorSidebarController {
  attachLibraryToComponent(kind: LibraryKind, componentId: string | null, libraryId: string, layerId?: string): void;
  detachLibraryToInline(kind: LibraryKind, componentId: string | null, layerId?: string): void;
  saveComponentSlotToLibrary(kind: LibraryKind, componentId: string | null, layerId?: string): void;
  createLibraryItem(kind: LibraryKind): string | null;
  libraryName(kind: LibraryKind, id: string): string;
  getSlotState(kind: LibraryKind, slot: SlotValue): { status: string; detail: string };
}

export function createEditorSidebarController(deps: EditorSidebarDeps): EditorSidebarController {
  const { appState } = deps;

  const controller: EditorSidebarController = {
    attachLibraryToComponent(kind, componentId, libraryId, layerId): void {
      deps.syncEditor();
      deps.commands.attachLibrary(componentId, kind, libraryId, layerId);
      deps.openComponentSlotEditor(componentId, kind, false, layerId);
    },
    detachLibraryToInline(kind, componentId, layerId): void {
      deps.syncEditor();
      deps.commands.detachLibrary(componentId, kind, layerId);
      deps.openComponentSlotEditor(componentId, kind, false, layerId);
    },
    saveComponentSlotToLibrary(kind, componentId, layerId): void {
      deps.syncEditor();
      const component = componentId ? appState.componentLibrary[componentId] : null;
      const scene = componentId ? component?.scene : appState.scene;
      if (!scene) return;

      const selectedLayerNode = layerId
        ? Object.values(scene.nodes).find(n => n.kind === 'layer' && n.layerId === layerId)
        : Object.values(scene.nodes).find(n => n.kind === 'layer');
      const layerDef = selectedLayerNode?.kind === 'layer' ? appState.layerLibrary[selectedLayerNode.layerId] : null;
      if (!layerDef) return;

      const slot = kind === 'geometry' ? layerDef.geometry : layerDef.material;
      if (slot.type !== 'inline') return;

      const ownerName = component?.name ?? 'Scene';
      const defaultName = `${ownerName} ${kind === 'geometry' ? 'Geometry' : 'Material'}`;
      const name = window.prompt(`Save ${kind} to library as:`, defaultName)?.trim();
      if (!name) return;

      if (!deps.commands.saveToLibrary(kind, name, componentId, kind, layerId)) return;
      deps.renderSceneTree();
      deps.renderEditorNavigation();
    },
    createLibraryItem(kind): string | null {
      const defaultName = kind === 'geometry'
        ? `Geometry ${Object.keys(appState.geometryLibrary.user).length + 1}`
        : `Material ${Object.keys(appState.materialLibrary.user).length + 1}`;
      const name = window.prompt(`Create ${kind} library item:`, defaultName)?.trim();
      if (!name) return null;

      return deps.commands.createLibraryItem(kind, name).id;
    },
    libraryName(kind, id): string {
      return libraryNameFor(appState, kind, id);
    },
    getSlotState(kind, slot): { status: string; detail: string } {
      return getSlotStateFor(appState, kind, slot);
    },
  };

  return controller;
}

export function initEditorSidebar(deps: EditorSidebarDeps): EditorSidebarController {
  return createEditorSidebarController(deps);
}
